import { IconExpandMore } from "@tine/designsystem-icons/sharp";
import { Checkbox } from "@tine/designsystem-ui-react";
import { cn } from "@tine/designsystem-utils";
import {
   type CSSProperties,
   type ForwardedRef,
   type ReactNode,
   forwardRef,
   useEffect,
   useLayoutEffect,
   useRef,
   useState
} from "react";
import useOutsideClick from "../../common/useOutsideClick";
import Button from "../Button";

type MultiSelectOption = {
   name: string;
   label: string;
};

type MultiSelectProps = {
   name: string;
   label: ReactNode;
   options: MultiSelectOption[];
   selected: string[];
   className?: string;
   onChange: (changed: MultiSelectChangePayload) => void;
};

export type MultiSelectChangePayload = {
   selected: string[];
   name: string;
   value: boolean;
};

type MultiSelectOptionsProps = {
   options: MultiSelectOption[];
   selected: string[];
   disabled?: boolean;
   className?: string;
   style?: CSSProperties;
   onChange: (changed: MultiSelectChangePayload) => void;
};

const baseListStyle =
   "tw-absolute tw-z-30 tw-block tw-left-0 tw-right-0 tw-w-fit tw-rounded-b tw-bg-surface-default tw-m-0 tw-p-0 focus:tw-outline-none tw-list-none";

const MultiSelectOptions = forwardRef<HTMLUListElement, MultiSelectOptionsProps>(
   (
      { options, selected, className = "", style, disabled, onChange }: MultiSelectOptionsProps,
      ref: ForwardedRef<HTMLUListElement>
   ) => {
      const optionList = options.map((option) => ({ ...option, checked: selected.includes(option.name) }));
      const listStyle = cn(baseListStyle, className);

      const createChangeHandler = (option: MultiSelectOption) => () => {
         const selectedSet = new Set<string>(selected);
         let isSelected: boolean;
         if (selectedSet.has(option.name)) {
            selectedSet.delete(option.name);
            isSelected = false;
         } else {
            selectedSet.add(option.name);
            isSelected = true;
         }
         onChange({ selected: Array.from(selectedSet), name: option.name, value: isSelected });
      };

      return (
         <ul className={listStyle} ref={ref} style={style} tabIndex={-1}>
            {optionList.map((option) => (
               <li key={option.name} className="tw-p-2 tw-text-nowrap">
                  <Checkbox
                     label={option.label}
                     isChecked={option.checked}
                     isDisabled={disabled}
                     onChange={createChangeHandler(option)}
                  />
               </li>
            ))}
         </ul>
      );
   }
);

const MultiSelect = ({ name, options, selected, onChange }: MultiSelectProps) => {
   const [open, setOpen] = useState<boolean>(false);
   const [buttonWidth, setButtonWidth] = useState<number | null>(null);
   const dropdownRef = useRef<HTMLUListElement>(null);
   const buttonRef = useRef<HTMLButtonElement>(null);
   const ref = useRef<HTMLDivElement>(null);
   useOutsideClick([ref], () => {
      setOpen(false);
   });

   useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
         if (event.key === "Escape") {
            setOpen(false);
         }
      };
      if (open) {
         window.addEventListener("keydown", handleKeyDown);
      } else {
         window.removeEventListener("keydown", handleKeyDown);
      }
      return () => {
         window.removeEventListener("keydown", handleKeyDown);
      };
   }, [open]);

   useLayoutEffect(() => {
      if (dropdownRef.current && buttonRef.current) {
         const { width: dropdownWidth } = dropdownRef.current.getBoundingClientRect();
         const { width: buttonWidth } = buttonRef.current.getBoundingClientRect();

         if (dropdownWidth > buttonWidth) {
            setButtonWidth(dropdownWidth);
         } else if (dropdownWidth < buttonWidth) {
            setButtonWidth(null);
         }
      }
   }, [options]);

   const additionalButtonProps = buttonWidth ? { style: { width: `${buttonWidth}px` } } : {};

   const listStyle = cn("tw-min-w-full", {
      "tw-h-0 tw-overflow-y-hidden ": !open
   });
   const buttonStyle = cn(
      "tw-flex tw-justify-between tw-min-w-full tw-bg-background-subtle tw-border tw-border-ink-border-subtle",
      {
         "tw-bg-button-primary-bg": open,
         _: !open
      }
   );
   const expandIconStyle = cn(
      "tw-transition-all tw-ease-in-out tw-duration-300 tw-text-base tw-w-5 tw-h-5",
      open ? "tw-rotate-180 tw-fill-ink-brand-inverted" : "tw-text-ink-default tw-rotate-0 tw-fill-ink-brand-default"
   );

   const containerStyle = cn("tw-relative tw-drop-shadow-sm", { "tw-z-20 tw-drop-shadow-md": open });

   const handleChange = (change: MultiSelectChangePayload) => {
      onChange(change);
   };

   const handleBlur = () => {
      const { current: element } = ref;
      requestAnimationFrame(() => {
         // Check if the new focused element is a child of the original container
         if (element && !element.contains(document.activeElement)) {
            setOpen(false);
         }
      });
   };

   return (
      <div className={containerStyle} ref={ref} onBlur={handleBlur}>
         <Button
            variant={open ? "primary" : "secondary"}
            className={buttonStyle}
            trackingName={name}
            {...additionalButtonProps}
            onClick={() => setOpen(!open)}
            ref={buttonRef}
         >
            {name}
            <IconExpandMore className={expandIconStyle} />
         </Button>
         <MultiSelectOptions
            options={options}
            selected={selected}
            disabled={!open}
            className={listStyle}
            onChange={handleChange}
            ref={dropdownRef}
         />
      </div>
   );
};

export default MultiSelect;
